import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Heart, AlertTriangle, CheckCircle, ChevronRight, ShieldAlert, Activity, Send, Mic, MicOff, Printer, RotateCcw, Bot, User, HeartPulse, Award, Radio } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import CPRQuickGuide from './CPRQuickGuide';
import EpiPenQuickGuide from './EpiPenQuickGuide';
import InfoGatheringPhase from './phases/InfoGatheringPhase';
import PreArrivalPhase, { emptyPreArrival, buildPreArrivalPrintHtml, formatChrono, SCENE_CHECKS } from './PreArrivalPhase';
import { CallNotesPanel, CallNotesCard, buildNotesHtml } from './callReport';

const PRIORITY_CONFIG = {
  1: { label: 'PRIORITÉ 1', title: 'Arrêt cardiaque suspecté', color: '#DC2626', bg: '#FEE2E2' },
  2: { label: 'PRIORITÉ 2', title: 'Douleur thoracique critique', color: '#F59E0B', bg: '#FEF3C7' },
  4: { label: 'PRIORITÉ 4', title: 'Évaluation en cours / stable', color: '#22C55E', bg: '#DCFCE7' },
};

const HealthSimulation = ({ protocol, studentName, studentNumber, scenario, onExit }) => {
  const navigate = useNavigate();
  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Protocol state
  const [currentPhaseIdx, setCurrentPhaseIdx] = useState(0);
  const [maxReachedIdx, setMaxReachedIdx] = useState(0);
  const [phaseAnswers, setPhaseAnswers] = useState({});
  const [redFlags, setRedFlags] = useState([]);
  const [currentPriority, setCurrentPriority] = useState(4);
  const [completed, setCompleted] = useState(false);
  const [sessionStartTime] = useState(Date.now());
  const [healthSessionId, setHealthSessionId] = useState(null);
  const [showCPRGuide, setShowCPRGuide] = useState(false);
  const [showEpiPenGuide, setShowEpiPenGuide] = useState(false);
  const [p1TriggeredAt, setP1TriggeredAt] = useState(null);
  const [p1EndSeconds, setP1EndSeconds] = useState(null);
  const [preArrival, setPreArrival] = useState(emptyPreArrival());
  // Transfert du 911 primaire (réalisme Québec) — poste ressource + numéro du répartiteur
  const [transferInfo] = useState(() => ({
    poste: String(Math.floor(1000 + Math.random() * 9000)),
    dispatcher: String(Math.floor(100 + Math.random() * 900)),
  }));
  const [callNotes, setCallNotes] = useState(() => [
    { at: 0, text: `Transfert 911 primaire — poste ressource ${transferInfo.poste}, répartiteur ${transferInfo.dispatcher}` },
  ]);
  const [showPreArrival, setShowPreArrival] = useState(false);
  const transferText = `Ici le 911, poste ressource ${transferInfo.poste}. L'adresse est non validée, le numéro de téléphone est non validé. Je vous transfère un citoyen — nature de l'appel : ${protocol.title.toLowerCase()}. Parlez madame, monsieur, vous êtes en communication avec le centre santé Montmorency.`;

  // Auto-detect cardiac arrest pattern from red flags
  const cprAlertActive = redFlags.some(rf => {
    const t = (rf || '').toLowerCase();
    return (
      t.includes('inconscient') || t.includes('ne respire') ||
      t.includes('arrêt') || t.includes('rcr') ||
      t.includes('respiration agonale') || t.includes('gasps')
    );
  });

  // Speech recognition
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Initialize chat session
  useEffect(() => {
    const initSession = async () => {
      try {
        const resp = await axios.post(`${API}/sessions`, {
          scenario_id: scenario.id,
          operator_name: studentName,
          student_number: studentNumber,
        });
        setChatSessionId(resp.data.id);

        // Also create health session (linked to the chat session for auto-evaluation)
        const hResp = await axios.post(`${API}/health/sessions`, {
          protocol_id: protocol.id,
          student_name: studentName,
          student_number: studentNumber,
          mode: 'simulation',
          chat_session_id: resp.data.id,
        });
        setHealthSessionId(hResp.data.session.id);
      } catch {
        toast.error('Erreur lors de la création de la session');
      }
    };
    initSession();
  }, [scenario.id, studentName, studentNumber, protocol.id]);

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message to AI citizen
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!currentMessage.trim() || !chatSessionId || sending) return;

    const userMsg = currentMessage.trim();
    setCurrentMessage('');
    setMessages(prev => [...prev, { role: 'dispatcher', text: userMsg }]);
    setSending(true);

    try {
      const resp = await axios.post(`${API}/chat/message`, {
        session_id: chatSessionId,
        message: userMsg,
        personality: 'calm',
        language: 'french',
      });
      setMessages(prev => [...prev, { role: 'citizen', text: resp.data.response }]);
    } catch {
      toast.error('Erreur de communication avec le citoyen');
    } finally {
      setSending(false);
    }
  };

  // Speech recognition
  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { toast.error('Reconnaissance vocale non supportée'); return; }
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-CA';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setCurrentMessage(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  // Protocol handlers
  const handleDecisionAnswer = (phase, answer) => {
    setPhaseAnswers(prev => ({ ...prev, [phase.id]: answer.label }));
    if (answer.priority_change && answer.priority_change < currentPriority) {
      setCurrentPriority(answer.priority_change);
    }
    if (answer.alert) {
      const aflag = phase.question + ' → ' + answer.label;
      setRedFlags(prev => prev.includes(aflag) ? prev : [...prev, aflag]);
    }
    const goP1 = answer.next === 'priority_1' || answer.priority_change === 1;
    if (answer.next === 'priority_1') {
      setCurrentPriority(1);
      const flag = phase.question + ' → ' + answer.label;
      setRedFlags(prev => prev.includes(flag) ? prev : [...prev, flag]);
    }
    if (goP1 && !p1TriggeredAt) {
      setCallNotes(prev => [...prev, { at: Math.floor((Date.now() - sessionStartTime) / 1000), text: '🚑 Ambulanciers affectés — Priorité 1' }]);
    }
    if (goP1) setP1TriggeredAt(prev => prev || Date.now());
    advancePhase(goP1);
  };

  const handleMultiAnswer = (phase, questionId, value, isRedFlag) => {
    setPhaseAnswers(prev => ({
      ...prev,
      [phase.id]: { ...(prev[phase.id] || {}), [questionId]: value }
    }));
    if (isRedFlag) {
      const flag = phase.questions.find(q => q.id === questionId)?.question || questionId;
      setRedFlags(prev => prev.includes(flag) ? prev : [...prev, flag]);
      if (currentPriority > 2) setCurrentPriority(2);
    }
  };

  const handleComparisonAnswer = (phase, answer) => {
    setPhaseAnswers(prev => ({ ...prev, [phase.id]: answer.label }));
    if (answer.id === 'cardiaque' && currentPriority > 2) setCurrentPriority(2);
    advancePhase();
  };

  const advancePhase = (goP1) => {
    if (currentPhaseIdx + 1 >= protocol.phases.length) {
      if (goP1 === true || currentPriority === 1) {
        // P1 active — open the pre-arrival panel instead of ending the call
        setCurrentPhaseIdx(protocol.phases.length);
        setMaxReachedIdx(prev => Math.max(prev, protocol.phases.length));
        setShowPreArrival(true);
      } else {
        completeSession();
      }
    } else {
      setCurrentPhaseIdx(prev => prev + 1);
      setMaxReachedIdx(prev => Math.max(prev, currentPhaseIdx + 1));
    }
  };

  const completeSession = async () => {
    setCompleted(true);
    setShowPreArrival(false);
    const duration = Math.floor((Date.now() - sessionStartTime) / 1000);
    const p1Seconds = p1TriggeredAt ? Math.floor((Date.now() - p1TriggeredAt) / 1000) : null;
    setP1EndSeconds(p1Seconds);
    if (healthSessionId) {
      await axios.put(`${API}/health/sessions/${healthSessionId}`, {
        status: 'completed',
        completed_at: new Date().toISOString(),
        duration_seconds: duration,
        answers: phaseAnswers,
        red_flags_detected: redFlags,
        final_priority: currentPriority,
        p1_triggered_at: p1TriggeredAt ? new Date(p1TriggeredAt).toISOString() : null,
        p1_to_end_seconds: p1Seconds,
        pre_arrival: p1TriggeredAt ? preArrival : null,
        call_notes: callNotes,
      }).catch(() => {});
    }
    if (chatSessionId) {
      await axios.put(`${API}/sessions/${chatSessionId}`, { status: 'completed' }).catch(() => {});
    }
  };

  const printSummary = () => {
    const pConfig = PRIORITY_CONFIG[currentPriority] || PRIORITY_CONFIG[4];
    const pData = protocol?.priorities?.[String(currentPriority)];
    const duration = Math.floor((Date.now() - sessionStartTime) / 1000);
    const formatTime = (s) => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');

    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Autorisez les popups'); return; }

    const rfHtml = redFlags.length > 0
      ? '<div style="margin:16px 0;padding:12px;border-left:4px solid #DC2626;background:#FEF2F2;border-radius:8px;">'
        + '<h4 style="color:#DC2626;font-size:14px;font-weight:bold;margin-bottom:8px;">Drapeaux rouges (' + redFlags.length + ')</h4>'
        + '<ul style="list-style:none;padding:0;">' + redFlags.map(f => '<li style="font-size:13px;color:#B91C1C;padding:2px 0;">⚠ ' + f + '</li>').join('') + '</ul></div>'
      : '';

    const transferHtml = '<div style="margin:4px 0;padding:8px 12px;border-radius:8px;background:#F5F3FF;border:1px solid #DDD6FE;">'
      + '<strong style="font-size:12px;color:#6D28D9;">911 primaire (transfert):</strong> '
      + '<span style="font-size:13px;font-style:italic;">' + transferText + '</span></div>';

    const chatHtml = messages.map(m =>
      '<div style="margin:4px 0;padding:8px 12px;border-radius:8px;background:' + (m.role === 'dispatcher' ? '#EFF6FF' : '#F0FDF4') + ';">'
      + '<strong style="font-size:12px;color:' + (m.role === 'dispatcher' ? '#1E40AF' : '#166534') + ';">' + (m.role === 'dispatcher' ? 'Répartiteur' : 'Citoyen') + ':</strong> '
      + '<span style="font-size:13px;">' + m.text + '</span></div>'
    ).join('');

    const resourcesHtml = pData
      ? '<div style="margin:16px 0;"><h4 style="font-size:14px;font-weight:bold;">Ressources</h4><ul>' + pData.resources.map(r => '<li style="font-size:13px;">' + r + '</li>').join('') + '</ul></div>'
      : '';

    const preArrivalHtml = p1TriggeredAt && p1EndSeconds != null
      ? buildPreArrivalPrintHtml(preArrival, p1EndSeconds, (protocol?.priorities?.['1']?.pre_arrival || []).length)
      : '';

    const notesHtml = buildNotesHtml(callNotes);

    printWindow.document.write('<!DOCTYPE html><html><head><title>Simulation Santé — ' + scenario.title + '</title>'
      + '<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Segoe UI,Arial,sans-serif;padding:30px;color:#1e293b;line-height:1.5;}'
      + '.header{text-align:center;margin-bottom:20px;padding-bottom:12px;border-bottom:2px solid #e2e8f0;}'
      + '.meta{display:flex;gap:20px;flex-wrap:wrap;padding:10px 14px;background:#f8fafc;border-radius:8px;margin-bottom:14px;font-size:13px;}</style></head><body>'
      + '<div class="header"><h1 style="font-size:18px;color:#002768;">Simulation Santé — ' + protocol.title + '</h1>'
      + '<p style="font-size:12px;color:#64748b;">Scénario: ' + scenario.title + '</p></div>'
      + '<div class="meta"><div><strong>Étudiant:</strong> ' + studentName + '</div>'
      + '<div><strong>Date:</strong> ' + new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + '</div>'
      + '<div><strong>Durée:</strong> ' + formatTime(duration) + '</div>'
      + '<div><strong>Mode:</strong> Simulation</div></div>'
      + '<div style="padding:12px;border:2px solid ' + pConfig.color + ';background:' + pConfig.bg + ';border-radius:10px;margin-bottom:14px;">'
      + '<h2 style="font-size:16px;color:' + pConfig.color + ';">' + pConfig.label + ' — ' + pConfig.title + '</h2></div>'
      + rfHtml + notesHtml + preArrivalHtml + resourcesHtml
      + '<h3 style="font-size:14px;margin:16px 0 8px;color:#002768;">Conversation</h3>' + transferHtml + chatHtml
      + '<div style="margin-top:20px;text-align:center;font-size:11px;color:#94a3b8;">Simulateur 911 — ' + new Date().toLocaleDateString('fr-FR') + '</div>'
      + '</body></html>');
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const currentPhase = protocol.phases[currentPhaseIdx];
  const pConfig = PRIORITY_CONFIG[currentPriority];

  // Render mini protocol phase
  const renderPhaseContent = (phase) => {
    if (!phase) return null;

    if (phase.type === 'info_gathering') {
      return (
        <InfoGatheringPhase
          phase={phase}
          answers={phaseAnswers}
          setAnswers={setPhaseAnswers}
          onComplete={advancePhase}
          compact
        />
      );
    }

    if (phase.type === 'open_question') {
      const noted = Array.isArray(phaseAnswers[phase.id]) ? phaseAnswers[phase.id] : [];
      const toggleNoted = (el) => setPhaseAnswers(prev => {
        const cur = Array.isArray(prev[phase.id]) ? prev[phase.id] : [];
        return { ...prev, [phase.id]: cur.includes(el) ? cur.filter(x => x !== el) : [...cur, el] };
      });
      return (
        <div>
          <div className="p-2 rounded bg-blue-50 border border-blue-200 mb-2">
            <p className="text-xs font-medium text-blue-800 italic">"{phase.question}"</p>
          </div>
          <div className="flex flex-wrap gap-1 mb-2">
            {phase.elements_recherches.map(el => (
              <label key={el} className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded cursor-pointer ${noted.includes(el) ? 'bg-blue-100 font-semibold text-blue-800' : 'bg-gray-50 hover:bg-gray-100'}`}>
                <input type="checkbox" className="rounded w-3 h-3" checked={noted.includes(el)} onChange={() => toggleNoted(el)} />
                {el}
              </label>
            ))}
          </div>
          <button onClick={advancePhase} className="w-full py-1.5 rounded text-xs font-semibold text-white" style={{ backgroundColor: '#002768' }}>
            Suivant <ChevronRight className="inline w-3 h-3" />
          </button>
        </div>
      );
    }

    if (phase.type === 'decision') {
      return (
        <div>
          <div className="p-2 rounded bg-blue-50 border border-blue-200 mb-2">
            <p className="text-xs font-medium text-blue-800 italic">"{phase.question}"</p>
          </div>
          <div className="space-y-1">
            {phase.answers.map(a => (
              <button
                key={a.id}
                onClick={() => handleDecisionAnswer(phase, a)}
                className="w-full text-left p-2 rounded border transition-all hover:shadow text-xs"
                style={{ borderColor: phaseAnswers[phase.id] === a.label ? '#2E6CF5' : a.alert ? '#FCA5A5' : '#E5E7EB', backgroundColor: phaseAnswers[phase.id] === a.label ? '#EFF6FF' : a.alert ? '#FEF2F2' : 'white' }}
                data-testid={`sim-answer-${a.id}`}
              >
                <span className="font-semibold">{a.label}</span>
                {phaseAnswers[phase.id] === a.label && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">✓</span>}
                {a.alert && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">ALERTE</span>}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (phase.type === 'multi_question') {
      return (
        <div className="space-y-2">
          {phase.questions.map(q => {
            const curVal = phaseAnswers[phase.id]?.[q.id];
            const isRedNow = (q.red_flag && curVal === 'oui') || (q.type === 'multi_choice' && (q.red_flag_options || []).includes(curVal));
            return (
            <div key={q.id} className="p-2 rounded border text-xs" style={{ borderColor: isRedNow ? '#FCA5A5' : '#E5E7EB' }}>
              <p className="font-medium text-gray-700 mb-1">
                {q.red_flag && <ShieldAlert className="inline w-3 h-3 mr-0.5 text-red-500" />}
                {q.question}
              </p>
              {q.type === 'scale' ? (
                <input type="range" min="0" max="10" value={phaseAnswers[phase.id]?.[q.id] ?? 5} className="w-full" onChange={e => handleMultiAnswer(phase, q.id, e.target.value, false)} />
              ) : q.type === 'multi_choice' ? (
                <div className="flex flex-wrap gap-1">
                  {(q.options || []).map(opt => {
                    const isSelected = curVal === opt;
                    const isRed = isSelected && (q.red_flag_options || []).includes(opt);
                    return (
                      <button
                        key={opt}
                        onClick={() => handleMultiAnswer(phase, q.id, opt, (q.red_flag_options || []).includes(opt))}
                        className="px-2 py-1 rounded text-[11px] font-semibold border transition-all"
                        style={{
                          borderColor: isRed ? '#DC2626' : isSelected ? '#2E6CF5' : '#E5E7EB',
                          backgroundColor: isRed ? '#FEE2E2' : isSelected ? '#EFF6FF' : 'white',
                          color: isRed ? '#DC2626' : isSelected ? '#2E6CF5' : '#6B7280',
                        }}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              ) : q.type === 'open_text' ? (
                <input
                  type="text"
                  value={curVal || ''}
                  onChange={e => handleMultiAnswer(phase, q.id, e.target.value, false)}
                  placeholder="Notez la réponse de l'appelant..."
                  className="w-full px-2 py-1.5 rounded border text-xs focus:outline-none transition-colors"
                  style={{ borderColor: (curVal || '').trim() ? '#2E6CF5' : '#E5E7EB' }}
                  data-testid={`sim-answer-${q.id}-text`}
                />
              ) : (
                <div className="flex gap-1">
                  {(q.type === 'yes_no_unknown' ? ['oui', 'non', 'inconnue'] : ['oui', 'non']).map(val => {
                    const labels = { oui: q.yes_label || 'Oui', non: q.no_label || 'Non', inconnue: 'Inconnue' };
                    const isSelected = phaseAnswers[phase.id]?.[q.id] === val;
                    const isRedActive = val === 'oui' && q.red_flag && isSelected;
                    const isUnknown = val === 'inconnue' && isSelected;
                    return (
                      <button
                        key={val}
                        onClick={() => handleMultiAnswer(phase, q.id, val, q.red_flag && val === 'oui')}
                        className="flex-1 py-1 rounded text-xs font-semibold border transition-all"
                        style={{
                          borderColor: isRedActive ? '#DC2626' : isUnknown ? '#F59E0B' : isSelected ? '#2E6CF5' : '#E5E7EB',
                          backgroundColor: isRedActive ? '#FEE2E2' : isUnknown ? '#FEF3C7' : isSelected ? '#EFF6FF' : 'white',
                          color: isRedActive ? '#DC2626' : isUnknown ? '#92400E' : isSelected ? '#2E6CF5' : '#6B7280',
                        }}
                      >
                        {labels[val]}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })}
          <button onClick={advancePhase} className="w-full py-1.5 rounded text-xs font-semibold text-white" style={{ backgroundColor: '#002768' }}>
            Suivant <ChevronRight className="inline w-3 h-3" />
          </button>
        </div>
      );
    }

    if (phase.type === 'comparison') {
      return (
        <div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="p-2 rounded bg-amber-50 border border-amber-200">
              <h4 className="text-[10px] font-bold text-amber-800 mb-1">Anxiété</h4>
              {phase.anxiety_symptoms.map(s => <p key={s} className="text-[10px] text-amber-700">• {s}</p>)}
            </div>
            <div className="p-2 rounded bg-red-50 border border-red-200">
              <h4 className="text-[10px] font-bold text-red-800 mb-1">Drapeaux rouges</h4>
              {phase.red_flags_cardiac.map(s => <p key={s} className="text-[10px] text-red-700">• {s}</p>)}
            </div>
          </div>
          <div className="space-y-1">
            {phase.answers.map(a => (
              <button key={a.id} onClick={() => handleComparisonAnswer(phase, a)} className="w-full text-left p-2 rounded border text-xs font-semibold hover:shadow transition-all" data-testid={`sim-answer-${a.id}`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="h-screen pt-20 flex flex-col" data-testid="health-simulation">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
        <div className="flex items-center gap-3">
          <Heart className="w-5 h-5 text-red-500" />
          <div>
            <h2 className="text-sm font-bold" style={{ color: '#002768' }}>{scenario.title}</h2>
            <span className="text-[11px] text-gray-400">Mode Simulation — {protocol.title}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: pConfig.bg, color: pConfig.color }}>
            {pConfig.label}
          </div>
          {redFlags.length > 0 && (
            <div className="px-2 py-1 rounded-full text-[11px] font-bold bg-red-100 text-red-700">
              {redFlags.length} alerte{redFlags.length > 1 ? 's' : ''}
            </div>
          )}
          {!completed && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Terminer l\'appel maintenant ? Le résumé de session sera généré.')) {
                  completeSession();
                }
              }}
              className="ml-2 px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:opacity-90 transition"
              style={{ backgroundColor: '#DC2626' }}
              data-testid="sim-end-call-button"
              title="Raccrocher l'appel — comme en situation réelle"
            >
              📞 Terminer l'appel
            </button>
          )}
        </div>
      </div>

      {/* Persistent P1 banner — ambulance dispatched, keep questioning */}
      {!completed && currentPriority === 1 && (
        <div
          className="px-4 py-2 border-b flex items-center gap-3"
          style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}
          data-testid="sim-priority-1-banner"
        >
          <div className="text-xl">🚨</div>
          <div className="flex-1">
            <div className="text-xs font-bold text-red-700">
              PRIORITÉ 1 ACTIVE — Ambulance envoyée
            </div>
            <div className="text-[11px] text-red-600">
              Continuez le questionnement : informations pour paramédics, consignes pré-arrivée, soutien au témoin. Raccrochez seulement à l'arrivée des secours.
            </div>
          </div>
          {!showPreArrival && (
            <button
              type="button"
              onClick={() => setShowPreArrival(true)}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white hover:opacity-90 transition"
              style={{ backgroundColor: '#DC2626' }}
              data-testid="sim-goto-pre-arrival-btn"
            >
              🚑 Consignes pré-arrivée →
            </button>
          )}
        </div>
      )}

      {/* Main content: Protocol left + Chat right */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Protocol */}
        <div className="w-[380px] border-r flex flex-col bg-gray-50 overflow-hidden">
          {/* Phase navigation - clickable for visited phases */}
          <div className="px-3 py-2 border-b bg-white">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {protocol.phases.map((phase, idx) => {
                const isDone = (idx < maxReachedIdx || completed) && idx !== currentPhaseIdx;
                const isActive = idx === currentPhaseIdx && !completed;
                const canNavigate = idx <= maxReachedIdx && idx !== currentPhaseIdx && !completed;
                return (
                  <button key={phase.id}
                    onClick={canNavigate ? () => setCurrentPhaseIdx(idx) : undefined}
                    disabled={!canNavigate && !isActive}
                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${canNavigate ? 'cursor-pointer hover:ring-2 hover:ring-offset-1' : ''}`}
                    style={{
                      backgroundColor: isDone ? '#22C55E' : isActive ? '#2E6CF5' : '#E5E7EB',
                      color: isDone || isActive ? 'white' : '#9CA3AF',
                      '--tw-ring-color': '#22C55E',
                    }}
                    title={canNavigate ? `Revenir à: Phase ${phase.number} — ${phase.title}` : phase.title}>
                    {isDone ? '✓' : phase.number}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Phase content */}
          <div className="flex-1 overflow-y-auto p-3">
            {completed ? (
              <div className="space-y-3">
                <div className="p-3 rounded-lg border-2" style={{ borderColor: pConfig.color, backgroundColor: pConfig.bg }}>
                  <h3 className="text-sm font-bold" style={{ color: pConfig.color }}>{pConfig.label}</h3>
                  <p className="text-xs" style={{ color: pConfig.color }}>{pConfig.title}</p>
                </div>
                {redFlags.length > 0 && (
                  <div className="p-2 rounded-lg bg-red-50 border border-red-200">
                    <h4 className="text-xs font-bold text-red-700 mb-1">Drapeaux rouges ({redFlags.length})</h4>
                    {redFlags.map((f, i) => <p key={`${i}-${f}`} className="text-[11px] text-red-600">⚠ {f}</p>)}
                  </div>
                )}
                {p1EndSeconds != null && (
                  <div className="p-2 rounded-lg bg-orange-50 border border-orange-200" data-testid="sim-summary-pre-arrival">
                    <h4 className="text-xs font-bold text-orange-800 mb-1">🚑 Volet P1 — Pré-arrivée</h4>
                    <p className="text-[11px] text-orange-700">Délai P1 → fin d'appel : <strong className="font-mono">{formatChrono(p1EndSeconds)}</strong></p>
                    <p className="text-[11px] text-orange-700">Consignes données : <strong>{Object.keys(preArrival.instructions).length} / {(protocol?.priorities?.['1']?.pre_arrival || []).length}</strong></p>
                    <p className="text-[11px] text-orange-700">Sécurisation des lieux : <strong>{Object.keys(preArrival.scene).length} / {SCENE_CHECKS.length}</strong></p>
                  </div>
                )}
                {protocol.priorities[String(currentPriority)] && (
                  <div className="p-2 rounded-lg bg-white border">
                    <h4 className="text-xs font-bold text-gray-700 mb-1">Ressources</h4>
                    {protocol.priorities[String(currentPriority)].resources.map(r => <p key={r} className="text-xs text-gray-600">• {r}</p>)}
                    <h4 className="text-xs font-bold text-gray-700 mt-2 mb-1">Consignes pré-arrivée</h4>
                    {protocol.priorities[String(currentPriority)].pre_arrival.map(c => <p key={c} className="text-xs text-gray-600">• {c}</p>)}
                  </div>
                )}
                {healthSessionId && (
                  <button onClick={() => navigate(`/auto-evaluation/health/${healthSessionId}`)} className="w-full flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#7C3AED' }} data-testid="sim-auto-eval-btn">
                    <Award className="w-3.5 h-3.5" /> Évaluation automatique (IA)
                  </button>
                )}
                <CallNotesCard notes={callNotes} />
                <div className="flex gap-2">
                  <button onClick={printSummary} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#002768' }} data-testid="sim-print-btn">
                    <Printer className="w-3.5 h-3.5" /> Imprimer
                  </button>
                  <button onClick={onExit} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="sim-exit-btn">
                    <RotateCcw className="w-3.5 h-3.5" /> Terminer
                  </button>
                </div>
              </div>
            ) : showPreArrival ? (
              <PreArrivalPhase
                protocol={protocol}
                p1TriggeredAt={p1TriggeredAt}
                preArrival={preArrival}
                setPreArrival={setPreArrival}
                onEndCall={completeSession}
                onBack={currentPhaseIdx < protocol.phases.length ? () => setShowPreArrival(false) : undefined}
                compact
              />
            ) : currentPhase ? (
              <Card className="border-0 shadow-none bg-transparent">
                <CardHeader className="py-2 px-0">
                  <div className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5" style={{ color: '#002768' }} />
                    <CardTitle className="text-xs" style={{ color: '#002768' }}>
                      Phase {currentPhase.number} — {currentPhase.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 px-0">
                  {renderPhaseContent(currentPhase)}
                </CardContent>
              </Card>
            ) : null}
            {!completed && (
              <CallNotesPanel
                notes={callNotes}
                onAddNote={(n) => setCallNotes(prev => [...prev, n])}
                getElapsed={() => Math.floor((Date.now() - sessionStartTime) / 1000)}
              />
            )}
          </div>
        </div>

        {/* RIGHT: Chat */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Initial scenario context */}
            <div className="text-center py-3">
              <div className="inline-block px-4 py-2 rounded-lg bg-gray-100 text-xs text-gray-500">
                Appel entrant — {scenario.title}
              </div>
            </div>
            {/* Transfert du 911 primaire */}
            <div className="flex justify-start">
              <div className="max-w-[88%] rounded-2xl rounded-bl-sm px-4 py-2.5 border-2" style={{ borderColor: '#7C3AED', backgroundColor: '#F5F3FF' }} data-testid="sim-transfer-message">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Radio className="w-3 h-3 text-purple-700" />
                  <span className="text-[10px] font-semibold text-purple-700">911 primaire — transfert d'appel</span>
                </div>
                <p className="text-sm text-gray-800 italic">« {transferText} »</p>
              </div>
            </div>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'dispatcher' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  msg.role === 'dispatcher'
                    ? 'rounded-br-sm text-white'
                    : 'rounded-bl-sm'
                }`} style={{
                  backgroundColor: msg.role === 'dispatcher' ? '#002768' : '#F1F5F9',
                  color: msg.role === 'dispatcher' ? 'white' : '#1E293B',
                }}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {msg.role === 'dispatcher' ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                    <span className="text-[10px] font-semibold opacity-75">{msg.role === 'dispatcher' ? 'Répartiteur' : 'Citoyen'}</span>
                  </div>
                  <p className="text-sm">{msg.text}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm px-4 py-3 bg-gray-100">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t p-3">
            <form onSubmit={sendMessage} className="flex gap-2">
              <button
                type="button"
                onClick={toggleListening}
                className={`p-2.5 rounded-lg transition-all ${isListening ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                data-testid="sim-mic-btn"
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <input
                type="text"
                value={currentMessage}
                onChange={e => setCurrentMessage(e.target.value)}
                placeholder={isListening ? 'Parlez maintenant...' : 'Posez votre question au citoyen...'}
                className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#2E6CF5' }}
                disabled={sending || completed}
                data-testid="sim-message-input"
              />
              <button
                type="submit"
                disabled={!currentMessage.trim() || sending || completed}
                className="px-4 py-2.5 text-white rounded-lg disabled:opacity-40 transition-all"
                style={{ backgroundColor: '#002768' }}
                data-testid="sim-send-btn"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>

            {/* CPR Quick Guide button — pulses if cardiac arrest pattern detected */}
            <button
              type="button"
              onClick={() => setShowCPRGuide(true)}
              className={`mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-bold shadow transition-all ${cprAlertActive ? 'animate-pulse ring-4 ring-red-300' : ''}`}
              style={{ backgroundColor: '#DC2626' }}
              data-testid="sim-open-cpr-guide-button"
              title="Guide rapide pour assister un RCR par téléphone"
            >
              <HeartPulse className="w-4 h-4" />
              {cprAlertActive ? '🫀 GUIDE RCR — URGENCE' : '🫀 Guide RCR (assistance téléphonique)'}
            </button>

            {/* EpiPen quick reference — Réaction allergique only */}
            {protocol?.id === 'reaction_allergique' && (
              <button
                type="button"
                onClick={() => setShowEpiPenGuide(true)}
                className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-bold shadow transition-all hover:brightness-110"
                style={{ backgroundColor: '#EA580C' }}
                data-testid="sim-open-epipen-guide-button"
                title="Pense-bête : guider un appelant avec un EpiPen"
              >
                💉 Pense-bête EpiPen (guider l&apos;appelant)
              </button>
            )}
          </div>
        </div>
      </div>

      <CPRQuickGuide open={showCPRGuide} onClose={() => setShowCPRGuide(false)} />
      <EpiPenQuickGuide open={showEpiPenGuide} onClose={() => setShowEpiPenGuide(false)} />
    </div>
  );
};

export default HealthSimulation;
